/**
 * The window manager's state: which windows exist, the current {@link Layout}, and the
 * commands to change it. The layout is remembered in the browser; it can also come from the
 * KDL config.
 *
 * Besides the app's own windows, others come and go at run time ({@link WindowManager.setExtra}:
 * the windows of plugins, named `plugin:<id>:<window>`). Their places in a saved layout are
 * kept even before they are known (the plugin list arrives after the page starts); one that
 * goes away while known (its plugin was removed or turned off) is closed.
 *
 * Each environment (coding, writing...) keeps a layout of its own ({@link WindowManager.useScope}):
 * switching environment puts away the current arrangement and brings back that environment's,
 * or makes its first one.
 *
 * Rendering lives in `Workspace.tsx`; this file has no DOM code.
 */
import { createMemo, createSignal, untrack, type Accessor, type Component } from "solid-js";
import { load, save } from "../shared/storage";
import type { CommandSet } from "../shared/commands";
import { WINDOW_GROUPS, type MenuSection } from "../shared/menus";
import * as L from "./layout";

/** A kind of window the manager can show. */
export interface WindowDef {
  /** Stable name, used in layouts and configs (`graph`, `inspector`...). */
  id: L.WindowId;
  /** Shown in the tab and the title bar. */
  title: string;
  /** The section of the Window menu it is listed in ("Graph", "Project"...; none: "Other"). */
  group?: string;
  /** The content. It gets everything it needs through `useApp()`, and its own name. */
  component: Component<{ window: L.WindowId }>;
  /**
   * Opened by the environments that list it (it is in no default layout): where it docks the
   * first time, beside a window (`zone` of that window's area); that window closed, as a tab of
   * the first group. `share`: how much of that area it takes (half when not said).
   */
  byEnvironment?: { beside: L.WindowId; zone: L.DropZone; share?: number };
}

/** Names of windows added at run time, kept in a layout even while unknown. */
export function isExtraWindow(id: L.WindowId): boolean {
  return id.startsWith("plugin:");
}

/** The commands and state of the window manager. */
export interface WindowManager {
  /** Every window that can be shown, in menu order: the app's, then the extra ones. */
  windows: Accessor<WindowDef[]>;
  /** Replaces the windows added at run time (plugin windows). Those that went away are closed. */
  setExtra(defs: WindowDef[]): void;
  /** The window definition for an id. */
  def(id: L.WindowId): WindowDef | undefined;
  /** The current layout (reactive). */
  layout: Accessor<L.Layout>;
  /** Replaces the layout through a function of the current one. */
  update(change: (layout: L.Layout) => L.Layout): void;
  /** Shows a window (or brings it forward if it is already shown). */
  open(id: L.WindowId): void;
  /** Hides a window. */
  close(id: L.WindowId): void;
  /** Shows a hidden window, hides a shown one. */
  toggle(id: L.WindowId): void;
  /** Detaches a window and floats it at a rectangle. */
  float(id: L.WindowId, rect: Omit<L.FloatingWindow, "window">): void;
  /** Goes back to the default layout (the scope's first one). */
  reset(): void;
  /**
   * Works in the layout of `scope` (an environment's id; `null`: the app's own) from now on:
   * the one saved for it, else `fresh(default layout)`; the first scope chosen keeps the
   * current layout instead (a layout arranged before environments existed is not lost).
   */
  useScope(scope: string | null, fresh: (base: L.Layout) => L.Layout): void;
  /** Windows made to crash on purpose (debug only, to test error isolation). */
  crashed: Accessor<ReadonlySet<L.WindowId>>;
  /** Makes a window crash (debug), or clears the crash so it can render again. */
  setCrashed(id: L.WindowId, crashed: boolean): void;
}

/** Where the layout is kept in the browser (followed by `:<scope>` for an environment's). */
const KEY = "developer-app-layout-v1";

/** Where the layout of `scope` is kept. */
function keyOf(scope: string | null): string {
  return scope === null ? KEY : `${KEY}:${scope}`;
}

/** Creates the window manager for `windows`, starting from the saved layout or `defaultLayout`. */
export function createWindowManager(builtIn: WindowDef[], defaultLayout: () => L.Layout): WindowManager {
  const ids = builtIn.map((w) => w.id);
  const saved = load<L.Layout | null>(KEY, null);
  const initial = L.sanitize(saved && typeof saved === "object" && "floating" in saved ? saved : defaultLayout(), ids, isExtraWindow);
  const [layout, setLayout] = createSignal<L.Layout>(initial);
  const [extra, setExtraDefs] = createSignal<WindowDef[]>([]);
  const windows = createMemo(() => [...builtIn, ...extra()]);
  const [crashed, setCrashedSet] = createSignal<ReadonlySet<string>>(new Set());
  /** The scope worked in (an environment), and how its first layout is made. */
  let scope: string | null = null;
  let fresh = (base: L.Layout) => base;
  /** Whether a scope was ever chosen (the first one keeps the layout on screen). */
  let scoped = false;

  /** Applies a change and remembers the new layout. */
  const update = (change: (layout: L.Layout) => L.Layout) => {
    const next = change(layout());
    if (next === layout()) return;
    setLayout(next);
    save(keyOf(scope), next);
  };
  /** The default layout, cleaned. */
  const base = () => L.sanitize(defaultLayout(), ids, isExtraWindow);

  return {
    windows,
    setExtra(defs) {
      // Untracked: an effect calling this must not re-run because of what it reads here.
      untrack(() => {
        const gone = extra().filter((old) => !defs.some((d) => d.id === old.id));
        setExtraDefs(defs);
        for (const def of gone) update((l) => (L.isOpen(l, def.id) ? L.close(l, def.id) : l));
      });
    },
    def: (id) => windows().find((w) => w.id === id),
    layout,
    update,
    open(id) {
      update((l) => (L.placeOf(l, id) === "floating" ? L.raise(l, id) : L.isOpen(l, id) ? L.activate(l, id) : L.open(l, id)));
    },
    close: (id) => update((l) => L.close(l, id)),
    toggle(id) {
      update((l) => (L.isOpen(l, id) ? L.close(l, id) : L.open(l, id)));
    },
    float: (id, rect) => update((l) => L.float(l, id, rect)),
    reset: () => update(() => L.sanitize(fresh(base()), ids, isExtraWindow)),
    useScope(next, make) {
      fresh = make;
      if (next === scope && scoped) return;
      const first = !scoped;
      scoped = true;
      scope = next;
      const saved = load<L.Layout | null>(keyOf(next), null);
      const kept = saved && typeof saved === "object" && "floating" in saved ? saved : first ? untrack(layout) : fresh(base());
      const clean = L.sanitize(kept, ids, isExtraWindow);
      setLayout(clean);
      save(keyOf(next), clean);
    },
    crashed,
    setCrashed(id, on) {
      const next = new Set(crashed());
      if (on) next.add(id);
      else next.delete(id);
      setCrashedSet(next);
    },
  };
}

/** The window manager's shortcut commands. `window.toggle` and `window.open` take the window's name. */
export function windowCommands(wm: WindowManager): CommandSet {
  return {
    "layout.reset": () => wm.reset(),
    "window.toggle": (window) => window && wm.toggle(window),
    "window.open": (window) => window && wm.open(window),
  };
}

/** The sections of the Window menu, in order; a window in another group goes last. */
export { WINDOW_GROUPS };

/**
 * The Window menu: every window, by group, checked when open (a click opens or closes it), then
 * the layout. Like Unreal's Window menu.
 */
export function windowMenus(wm: WindowManager): MenuSection[] {
  /** The windows of a group, as lines. */
  const lines = (group: string) =>
    wm
      .windows()
      .filter((w) => (w.group ?? "Other") === group)
      .map((w) => ({ label: w.title, checked: L.isOpen(wm.layout(), w.id), action: () => wm.toggle(w.id) }));
  const groups = [...WINDOW_GROUPS, "Other"];
  return [
    ...groups.map((group, i) => ({ menu: "window" as const, section: group, order: 10 + i, entries: () => lines(group) })),
    { menu: "window", section: "Layout", order: 90, entries: () => [{ label: "Reset the layout", command: "layout.reset" }] },
  ];
}
